import { swaDate } from "@swa-ui/date";

import { getTripType } from "../utils";
import { AIR_SEAT_TYPES } from "./defines/airSeatTypes";

const ABBREVIATED_SEAT_NAMES = {
  EXIT: "exit",
  EXTRA_LEGROOM: "elr",
  PREFERED: "premium",
  STANDARD: "standard",
};

export const getBookingCurveDetails = (departureDate) => {
  const daystoDepart = swaDate(departureDate).diff(swaDate(), "days");

  return `${daystoDepart}`;
};

export const getTimeToDeparture = (departureTime) => {
  const timetoDepart = swaDate(departureTime).diff(swaDate(), "minutes");

  return `${timetoDepart}`;
};

export const getSeatMapDetails = (seats = {}, selectedSegment = 0) => {
  const seatMapObject = {
    extraLegRoomSeats: 0,
    exitSeats: 0,
    peferredSeats: 0,
    standardSeats: 0,
    totalSeats: 0,
  };
  const rows = seats[selectedSegment]?.rows;

  if (rows) {
    for (const row of rows) {
      if (row?.seats && Array.isArray(row?.seats) && row?.seats.length > 0) {
        for (const seat of row.seats) {
          if (!seat.characteristics || seat.characteristics.includes("NO_SEAT")) {
            continue;
          }
          seatMapObject.totalSeats += 1;

          if (seat.available) {
            if (seat.characteristics.includes(AIR_SEAT_TYPES.STANDARD)) {
              seatMapObject.standardSeats += 1;
            } else if (seat.characteristics.includes(AIR_SEAT_TYPES.EXIT_ROW)) {
              seatMapObject.exitSeats += 1;
            } else if (seat.characteristics.includes(AIR_SEAT_TYPES.EXTRA_LEGROOM)) {
              seatMapObject.extraLegRoomSeats += 1;
            } else if (seat.characteristics.includes(AIR_SEAT_TYPES.PREFERRED)) {
              seatMapObject.peferredSeats += 1;
            }
          }
        }
      }
    }
  }

  return {
    seats_availableexit: `${seatMapObject.exitSeats}`,
    seats_availableexlegroom: `${seatMapObject.extraLegRoomSeats}`,
    seats_availablepremium: `${seatMapObject.peferredSeats}`,
    seats_availablestandard: `${seatMapObject.standardSeats}`,
    seats_total: `${seatMapObject.totalSeats}`,
  };
};

export const getFlightDetails = (flightDetails, selectedSegment) => {
  const selectedFlight = flightDetails?.find((flight) => flight.id === selectedSegment + 1);
  let result = {};

  if (selectedFlight) {
    const flightSegment = selectedFlight?.flightSegment;

    result = {
      airport_codes: `${flightSegment.origin}:${flightSegment.destination}`,
      lengthofflight: `${flightSegment.duration}`,
    };
  }

  return result;
};

export const getAbbreviatedSeatsOption = (seatCharacteristics = []) => {
  let abbreviatedSeatName = ABBREVIATED_SEAT_NAMES.STANDARD;

  if (seatCharacteristics.includes(AIR_SEAT_TYPES.EXIT_ROW)) {
    abbreviatedSeatName = ABBREVIATED_SEAT_NAMES.EXIT;
  } else if (seatCharacteristics.includes(AIR_SEAT_TYPES.EXTRA_LEGROOM)) {
    abbreviatedSeatName = ABBREVIATED_SEAT_NAMES.EXTRA_LEGROOM;
  } else if (seatCharacteristics.includes(AIR_SEAT_TYPES.PREFERRED)) {
    abbreviatedSeatName = ABBREVIATED_SEAT_NAMES.PREFERED;
  }

  return abbreviatedSeatName;
};

export const getProductString = (products = []) =>
  products
    .map(({ category = "", product = "", quantity = "", price = "" }) =>
      [category, product, quantity, price].join(";")
    )
    .join(",");

export const getSeatProducts = (seatProducts, tripType, isChangeFlow) => {
  const { passengers = [], seats = [], bounds = [] } = seatProducts ?? {};
  const productSeatArray = [];
  const adultPassengers = passengers.filter((passenger) => passenger.type === "ADULT");

  bounds.forEach((bound) => {
    bound?.segments?.forEach((boundSegment) => {
      const abbreviatedTripType = getTripType(tripType);
      const { arrival = {}, departure = {}, id = "" } = boundSegment || {};
      const { airport: departureAirport = "" } = departure;
      const { airport: arrivalAirport = "" } = arrival;
      const segmentsWithSelectedSeats = seats.filter((seat) => seat.segmentId === id);

      segmentsWithSelectedSeats.forEach((seat) => {
        productSeatArray.push({
          airportcodes: `${departureAirport}:${arrivalAirport}`,
          category: "seats",
          price: seat?.price?.baseFare?.amount || "0.00",
          product:
            `${getAbbreviatedSeatsOption(seat.characteristics)}:${abbreviatedTripType}:` +
            `${departureAirport}:${arrivalAirport}:${seat.row}:${seat.column}`,
          seatcolumn: seat.column,
          seatrow: seat.row,
          seattype: getAbbreviatedSeatsOption(seat.characteristics),
          triptype: abbreviatedTripType,
          quantity: "1",
        });
      });

      if (segmentsWithSelectedSeats.length !== adultPassengers.length && !isChangeFlow) {
        const autoAssignedArray = new Array(
          adultPassengers.length - segmentsWithSelectedSeats.length
        ).fill({
          airportcodes: `${departureAirport}:${arrivalAirport}`,
          category: "seats",
          price: "0.00",
          product: `auto:${abbreviatedTripType}:` + `${departureAirport}:${arrivalAirport}:`,
          seatcolumn: "",
          seatrow: "",
          seattype: "auto",
          triptype: abbreviatedTripType,
          quantity: "1",
        });

        productSeatArray.push(...autoAssignedArray);
      }
    });
  });

  return productSeatArray;
};

export const getTargetSeatString = (passengersDetails = []) => {
  const targetObject = {};

  passengersDetails.forEach(({ tripDetails = [] }) => {
    tripDetails.forEach((tripDetail) => {
      const {
        arrivalStation,
        assignedSeat,
        characteristics,
        departureStation,
        openSeating,
        seatAssignedAtCheckIn,
        seatSelectionSkipped,
      } = tripDetail;
      const airportCode = `${departureStation}:${arrivalStation}`;
      const seatAssignment =
        seatSelectionSkipped || openSeating
          ? "NONE"
          : seatAssignedAtCheckIn
          ? "AUTO"
          : `${assignedSeat}${getAbbreviatedSeatsOption(characteristics)}`;

      if (!targetObject[airportCode]) {
        targetObject[airportCode] = [airportCode];
      }

      targetObject[airportCode].push(seatAssignment);
    });
  });

  return Object.values(targetObject).join("|");
};
